import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productAPI, reviewAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Spinner, PriceRow, StatusBadge } from '../components/shared/index';
import toast from 'react-hot-toast';
import { addDays, format } from 'date-fns';

const DURATIONS = [
  { key: '2days',  label: '2 Days',  days: 2,  priceKey: 'twoDays'  },
  { key: '5days',  label: '5 Days',  days: 5,  priceKey: 'fiveDays' },
  { key: '1week',  label: '1 Week',  days: 7,  priceKey: 'oneWeek'  },
  { key: '2weeks', label: '2 Weeks', days: 14, priceKey: 'twoWeeks' }
];

export default function ProductDetailPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [product,         setProduct]         = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [addingToCart,    setAddingToCart]    = useState(false);
  const [selectedSize,    setSelectedSize]    = useState('');
  const [selectedDuration,setSelectedDuration]= useState(null);
  const [deliveryDate,    setDeliveryDate]    = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  });
  const [activeImg,       setActiveImg]       = useState(0);
  const [availability,    setAvailability]    = useState(null);
  const [checkingAvail,   setCheckingAvail]   = useState(false);
  // Reviews
  const [reviews,         setReviews]         = useState([]);
  const [reviewTotal,     setReviewTotal]      = useState(0);
  const [reviewBreakdown, setReviewBreakdown]  = useState({ 1:0,2:0,3:0,4:0,5:0 });
  const [reviewsLoading,  setReviewsLoading]   = useState(false);
  const [canReview,       setCanReview]        = useState(false);
  const [hasReviewed,     setHasReviewed]      = useState(false);
  const [reviewOrderId,   setReviewOrderId]    = useState(null);
  const [showReviewForm,  setShowReviewForm]   = useState(false);
  const [reviewRating,    setReviewRating]     = useState(0);
  const [reviewTitle,     setReviewTitle]      = useState('');
  const [reviewBody,      setReviewBody]       = useState('');
  const [reviewOccasion,  setReviewOccasion]   = useState('');
  const [reviewFit,       setReviewFit]        = useState('');
  const [submittingReview,setSubmittingReview] = useState(false);
  const [reviewSort,      setReviewSort]       = useState('recent');
  const [reviewPage,      setReviewPage]       = useState(1);
  // Related products
  const [related,         setRelated]          = useState([]);
  const [showSizeGuide,   setShowSizeGuide]    = useState(false);

  useEffect(() => {
    productAPI.getOne(id)
      .then(({ data }) => setProduct(data.product))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch related products when product is loaded
  useEffect(() => {
    if (!product) return;
    productAPI.getAll({ category: product.category, limit: 4 })
      .then(({ data }) => {
        // exclude current product
        setRelated((data.products || []).filter(p => p._id !== product._id).slice(0, 4));
      })
      .catch(() => {});
  }, [product]);

  const fetchReviews = async (page = 1, sort = reviewSort) => {
    setReviewsLoading(true);
    try {
      const { data } = await reviewAPI.getReviews(id, { page, limit: 5, sort });
      setReviews(data.reviews);
      setReviewTotal(data.total);
      setReviewBreakdown(data.breakdown);
    } catch {}
    finally { setReviewsLoading(false); }
  };

  useEffect(() => { if (id) fetchReviews(1, 'recent'); }, [id]);

  useEffect(() => {
    if (user && id) {
      reviewAPI.canReview(id)
        .then(({ data }) => {
          setCanReview(data.canReview);
          setHasReviewed(data.hasReviewed);
          setReviewOrderId(data.orderId);
        })
        .catch(() => {});
    }
  }, [user, id]);

  const handleReviewSort = (s) => { setReviewSort(s); setReviewPage(1); fetchReviews(1, s); };

  const handleSubmitReview = async () => {
    if (!reviewRating) { toast.error('Please select a star rating'); return; }
    setSubmittingReview(true);
    try {
      await reviewAPI.submit(id, {
        orderId:  reviewOrderId,
        rating:   reviewRating,
        title:    reviewTitle,
        body:     reviewBody,
        occasion: reviewOccasion,
        fit:      reviewFit || null
      });
      toast.success('Review submitted! Thank you.');
      setShowReviewForm(false);
      setHasReviewed(true); setCanReview(false);
      fetchReviews(1, reviewSort);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  useEffect(() => {
    if (selectedSize && selectedDuration && deliveryDate) checkAvailability();
  }, [selectedSize, selectedDuration, deliveryDate]);

  const checkAvailability = async () => {
    setCheckingAvail(true);
    try {
      const { data } = await productAPI.checkAvailability(id, { deliveryDate, duration: selectedDuration, size: selectedSize });
      setAvailability(data);
    } catch (e) { console.error(e); }
    finally { setCheckingAvail(false); }
  };

  const handleAddToCart = async () => {
    if (!user) { navigate('/login'); return; }
    if (!selectedSize) { toast.error('Please select a size'); return; }
    if (!selectedDuration) { toast.error('Please select a rental duration'); return; }
    if (availability && !availability.available) { toast.error('This item is not available for selected dates'); return; }
    setAddingToCart(true);
    try {
      await addToCart({ productId: product._id, size: selectedSize, rentalDuration: selectedDuration, deliveryDate });
      toast.success('Added to cart.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add to cart');
    } finally { setAddingToCart(false); }
  };

  const handleOrderNow = async () => {
    if (!user) { navigate('/login'); return; }
    if (!selectedSize) { toast.error('Please select a size'); return; }
    if (!selectedDuration) { toast.error('Please select a rental duration'); return; }
    if (availability && !availability.available) { toast.error('This item is not available for selected dates'); return; }
    setAddingToCart(true);
    try {
      await addToCart({ productId: product._id, size: selectedSize, rentalDuration: selectedDuration, deliveryDate });
      navigate('/cart');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to process order');
    } finally { setAddingToCart(false); }
  };

  if (loading) return <Spinner size="lg" centered />;
  if (!product) return <div className="container section">Product not found.</div>;

  const dur        = DURATIONS.find(d => d.key === selectedDuration);
  const delivery   = new Date(deliveryDate);
  const rentalEnd  = dur ? addDays(delivery, dur.days) : null;
  const returnDate = rentalEnd ? addDays(rentalEnd, 1) : null;
  const bufferEnd  = returnDate ? addDays(returnDate, 2) : null;
  const rentalPrice = dur ? product.rentalPrices?.[dur.priceKey] : 0;
  const total       = rentalPrice + product.securityDeposit;
  const inWishlist  = isInWishlist(product._id);
  const minDelivery = new Date(); minDelivery.setDate(minDelivery.getDate() + 1);

  return (
    <div className="container fade-in" style={{ padding: 'clamp(16px, 4vw, 48px) clamp(12px, 4vw, 28px)' }}>
      <style>{`
        @media(max-width: 768px) {
          .pdp-layout {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .pdp-layout > div:first-child .pdp-main-img {
            height: 75vw !important;
            max-height: 420px !important;
          }
        }
      `}</style>
      <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }}>
        ← Back
      </button>

      <div style={s.layout} className="pdp-layout">
        {/* ── Gallery ─────────────────────────────────────────────────── */}
        <div>
          <div style={s.mainImg} className="pdp-main-img">
            {product.images?.[activeImg]?.url ? (
              <img src={product.images[activeImg].url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: 'var(--cream-dark)' }} />
            )}
          </div>
          {product.images?.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {product.images.map((img, i) => (
                <div key={i} onClick={() => setActiveImg(i)} style={{ ...s.thumb, borderColor: i === activeImg ? 'var(--gold)' : 'var(--border)' }}>
                  <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Details ─────────────────────────────────────────────────── */}
        <div>
          <span className="tag">{product.category}</span>
          {!product.isActive && <span className="tag tag-red" style={{ marginLeft: 8 }}>Unavailable</span>}
          <h1 style={s.productTitle}>{product.name}</h1>

          {product.brand && <p style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '0.1em', marginBottom: 12 }}>By {product.brand}</p>}

          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.8, marginBottom: 20 }}>{product.description}</p>

          {product.material && (
            <p style={{ fontSize: 12, marginBottom: 18 }}>
              <span className="text-muted">Material: </span>{product.material}
            </p>
          )}

          <hr className="divider" />

          {/* Size Selection */}
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Select Size</label>
              <button onClick={() => setShowSizeGuide(true)}
                style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                Size Guide
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {product.sizes?.map(sz => {
                const stock = product.stockBySize?.[sz] || 0;
                const isLow = stock > 0 && stock <= 2;
                return (
                  <div key={sz} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <button
                      disabled={stock === 0}
                      onClick={() => setSelectedSize(sz)}
                      style={{ ...s.sizeBtn, borderColor: selectedSize === sz ? 'var(--gold)' : isLow ? '#e65100' : 'var(--border)', background: selectedSize === sz ? 'var(--gold-pale)' : 'white', opacity: stock === 0 ? 0.35 : 1, cursor: stock === 0 ? 'not-allowed' : 'pointer', position: 'relative' }}
                    >
                      {sz}
                      {stock === 0 && (
                        <span style={{ position: 'absolute', top: -1, right: -1, width: 7, height: 7, borderRadius: '50%', background: '#b71c1c' }} />
                      )}
                    </button>
                    {isLow  && <span style={{ fontSize: 8, fontWeight: 700, color: '#e65100', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{stock} left</span>}
                    {stock === 0 && <span style={{ fontSize: 8, color: 'var(--muted)', letterSpacing: '0.04em' }}>Out</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Size Guide Modal */}
          {showSizeGuide && <SizeGuideModal onClose={() => setShowSizeGuide(false)} category={product.category} />}

          {/* Duration Selection */}
          <div className="form-group mt-16">
            <label className="form-label">Rental Duration</label>
            {DURATIONS.map(d => {
              const dDelivery = new Date(deliveryDate);
              const dReturn   = addDays(dDelivery, d.days + 1);
              const isSelected = selectedDuration === d.key;
              return (
                <div key={d.key} onClick={() => setSelectedDuration(d.key)}
                  style={{ ...s.durationOption, borderColor: isSelected ? 'var(--gold)' : 'var(--border)', background: isSelected ? 'var(--gold-pale)' : 'white' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{d.label}</div>
                    {deliveryDate && (
                      <div style={{ fontSize: 10, color: isSelected ? 'var(--ink)' : 'var(--muted)', marginTop: 3 }}>
                        Delivery {format(dDelivery, 'dd MMM')} → Return by {format(dReturn, 'dd MMM')}
                      </div>
                    )}
                  </div>
                  <span style={{ color: 'var(--gold)', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>₹{product.rentalPrices?.[d.priceKey]?.toLocaleString()}</span>
                </div>
              );
            })}
          </div>

          {/* Delivery Date */}
          <div className="form-group mt-16">
            <label className="form-label">Delivery Date</label>
            <input
              type="date"
              className="input"
              value={deliveryDate}
              min={minDelivery.toISOString().split('T')[0]}
              onChange={e => setDeliveryDate(e.target.value)}
              style={{ maxWidth: 240 }}
            />
          </div>

          {/* Availability */}
          {checkingAvail && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 12, marginBottom: 14 }}>
              <Spinner size="sm" /> Checking availability...
            </div>
          )}
          {availability && !checkingAvail && (
            <div style={{ marginBottom: 14 }}>
              <span className={`tag ${availability.available ? 'tag-green' : 'tag-red'}`}>
                {availability.available ? 'Available for selected dates' : 'Not available — try different dates'}
              </span>
            </div>
          )}

          {/* Rental Summary */}
          {dur && (
            <div style={s.summary}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14 }}>
                Rental Summary
              </div>
              {[
                ['Delivery Date', format(delivery, 'dd MMM yyyy')],
                ['Rental Until',  rentalEnd ? format(rentalEnd, 'dd MMM yyyy') : '—'],
                ['Return By',     returnDate ? format(returnDate, 'dd MMM yyyy') : '—'],
                ['Buffer Until',  bufferEnd  ? format(bufferEnd,  'dd MMM yyyy') : '—'],
              ].map(([label, val]) => (
                <div key={label} style={s.summaryRow}>
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{val}</span>
                </div>
              ))}
              <hr className="divider" style={{ margin: '12px 0' }} />
              <PriceRow label="Rental Price"                  amount={rentalPrice} />
              <PriceRow label="Security Deposit (refundable)" amount={product.securityDeposit} />
              <PriceRow label="Grand Total"                   amount={total} bold gold />
            </div>
          )}

          {/* CTA Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Order Now - primary action */}
            <button
              className="btn btn-gold btn-lg"
              style={{ width: '100%' }}
              onClick={handleOrderNow}
              disabled={addingToCart || !product.isActive}
            >
              {addingToCart ? 'Processing...' : product.isActive ? 'Order Now' : 'Unavailable'}
            </button>
            {/* Add to Cart + Wishlist row */}
            <div style={s.ctaRow}>
              <button
                className="btn btn-outline"
                style={{ flex: 1 }}
                onClick={handleAddToCart}
                disabled={addingToCart || !product.isActive}
              >
                {addingToCart ? 'Adding...' : 'Add to Cart'}
              </button>
              <button
                onClick={() => { if (!user) { navigate('/login'); return; } toggleWishlist(product._id); }}
                style={{ ...s.wishBtn, borderColor: inWishlist ? '#c0392b' : 'var(--border)', color: inWishlist ? '#c0392b' : 'var(--muted)' }}
              >
                {inWishlist ? '♥' : '♡'}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 10, textAlign: 'center', color: 'var(--muted)', letterSpacing: '0.08em', marginTop: 10 }}>
            No late fine policy · 1–2 day buffer after return
          </p>
        </div>
      </div>
      {/* ── Related Products ────────────────────────────────────────────── */}
      {related.length > 0 && (
        <div style={{ marginTop: 48, paddingTop: 36, borderTop: '1px solid var(--border)' }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>You May Also Like</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, margin: 0 }}>More in {product.category}</h2>
          </div>
          <div className="related-products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {related.map(p => {
              const minP = p.rentalPrices?.twoDays || 0;
              const img  = p.images?.find(i => i.isPrimary)?.url || p.images?.[0]?.url;
              return (
                <div key={p._id} onClick={() => navigate(`/product/${p._id}`)}
                  style={{ cursor: 'pointer', border: '1px solid var(--border)', background: 'white', transition: 'box-shadow 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                  <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: 'var(--cream-dark)' }}>
                    {img
                      ? <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }}
                          onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                          onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
                      : <div style={{ width: '100%', height: '100%', background: 'var(--cream-dark)' }} />
                    }
                  </div>
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    {p.averageRating > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                        <StarRow rating={p.averageRating} size={11} />
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{p.averageRating}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700 }}>from ₹{minP.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Reviews Section ─────────────────────────────────────────────── */}
      <div style={{ marginTop: 56, paddingTop: 40, borderTop: '1px solid var(--border)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>Customer Reviews</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, margin: 0, lineHeight: 1 }}>
              {product.averageRating > 0 ? (
                <><StarRow rating={product.averageRating} size={24} /> <span style={{ fontSize: 28, marginLeft: 10, verticalAlign: 'middle' }}>{product.averageRating}</span></>
              ) : 'No reviews yet'}
            </h2>
            {reviewTotal > 0 && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>{reviewTotal} verified rental review{reviewTotal !== 1 ? 's' : ''}</div>}
          </div>
          {canReview && !showReviewForm && (
            <button onClick={() => setShowReviewForm(true)}
              style={{ padding: '10px 24px', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--gold)', border: 'none', cursor: 'pointer' }}>
              Write a Review
            </button>
          )}
          {hasReviewed && (
            <div style={{ fontSize: 11, color: '#4caf50', fontWeight: 600 }}>✓ You reviewed this product</div>
          )}
        </div>

        {/* Rating breakdown bar */}
        {reviewTotal > 0 && (
          <div className="review-breakdown-row" style={{ display: 'flex', gap: 32, marginBottom: 32, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 180 }}>
              {[5,4,3,2,1].map(star => {
                const count = reviewBreakdown[star] || 0;
                const pct   = reviewTotal ? Math.round((count / reviewTotal) * 100) : 0;
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', width: 14, textAlign: 'right' }}>{star}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#e8a838" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    <div style={{ flex: 1, background: 'var(--cream-dark)', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: pct + '%', height: '100%', background: 'var(--gold)', borderRadius: 3, transition: 'width 0.4s' }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--muted)', width: 28 }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Write review form */}
        {showReviewForm && (
          <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderTop: '2px solid var(--ink)', padding: 24, marginBottom: 32 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--ink)', marginBottom: 18 }}>Your Review</div>

            {/* Star picker */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Rating *</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewRating(s)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 28, lineHeight: 1, color: s <= reviewRating ? '#e8a838' : '#ddd', transition: 'color 0.1s' }}>
                    ★
                  </button>
                ))}
                {reviewRating > 0 && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6, alignSelf: 'center' }}>{['','Poor','Fair','Good','Very Good','Excellent'][reviewRating]}</span>}
              </div>
            </div>

            {/* Fit */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Fit</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['runs_small','Runs Small'],['true_to_size','True to Size'],['runs_large','Runs Large']].map(([v,l]) => (
                  <button key={v} onClick={() => setReviewFit(reviewFit === v ? '' : v)}
                    style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', border: '1px solid ' + (reviewFit === v ? 'var(--ink)' : 'var(--border)'), background: reviewFit === v ? 'var(--ink)' : 'transparent', color: reviewFit === v ? 'var(--cream)' : 'var(--muted)', cursor: 'pointer', transition: 'all 0.15s' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Occasion worn for</div>
              <input className="input" placeholder="e.g. Wedding, Party, Interview..." value={reviewOccasion} onChange={e => setReviewOccasion(e.target.value)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Review title</div>
              <input className="input" placeholder="Summarise your experience" value={reviewTitle} onChange={e => setReviewTitle(e.target.value)} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Your review</div>
              <textarea className="input" rows={4} placeholder="Tell others about the quality, fit, and experience..." value={reviewBody} onChange={e => setReviewBody(e.target.value)} style={{ resize: 'vertical', minHeight: 90 }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSubmitReview} disabled={submittingReview || !reviewRating}
                style={{ flex: 2, padding: '11px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: reviewRating ? 'var(--ink)' : 'var(--cream-dark)', color: reviewRating ? 'var(--gold)' : 'var(--muted)', border: 'none', cursor: reviewRating ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}>
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
              <button onClick={() => setShowReviewForm(false)}
                style={{ flex: 1, padding: '11px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'none', color: 'var(--muted)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Sort bar */}
        {reviewTotal > 0 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '0.1em', marginRight: 8 }}>SORT</span>
            {[['recent','Recent'],['highest','Highest'],['lowest','Lowest']].map(([v,l]) => (
              <button key={v} onClick={() => handleReviewSort(v)}
                style={{ padding: '5px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', border: '1px solid ' + (reviewSort === v ? 'var(--ink)' : 'var(--border)'), background: reviewSort === v ? 'var(--ink)' : 'transparent', color: reviewSort === v ? 'var(--cream)' : 'var(--muted)', cursor: 'pointer', transition: 'all 0.15s' }}>
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Review list */}
        {reviewsLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', border: '1px solid var(--border)', background: 'white' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontStyle: 'italic', color: 'var(--cream-dark)', marginBottom: 8 }}>No reviews yet</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Be the first to review after your rental</div>
          </div>
        ) : (
          <div>
            {reviews.map(r => (
              <div key={r._id} style={{ padding: '20px 0', borderBottom: '1px solid var(--cream-dark)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <StarRow rating={r.rating} size={14} />
                      {r.title && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{r.title}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)' }}>{r.user?.name}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', border: '1px solid #c8e6c9' }}>✓ VERIFIED RENTAL</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {r.fit && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '3px 8px', border: '1px solid var(--border)', color: 'var(--muted)', textTransform: 'uppercase' }}>
                        {{ true_to_size:'True to Size', runs_small:'Runs Small', runs_large:'Runs Large' }[r.fit]}
                      </span>
                    )}
                    {r.occasion && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '3px 8px', border: '1px solid var(--border)', color: 'var(--muted)' }}>
                        {r.occasion}
                      </span>
                    )}
                  </div>
                </div>
                {r.body && <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: 0 }}>{r.body}</p>}
              </div>
            ))}

            {/* Pagination */}
            {Math.ceil(reviewTotal / 5) > 1 && (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 24 }}>
                {Array.from({ length: Math.ceil(reviewTotal / 5) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => { setReviewPage(p); fetchReviews(p, reviewSort); }}
                    style={{ width: 32, height: 32, fontSize: 11, fontWeight: 600, border: '1px solid ' + (reviewPage === p ? 'var(--ink)' : 'var(--border)'), background: reviewPage === p ? 'var(--ink)' : 'transparent', color: reviewPage === p ? 'var(--cream)' : 'var(--muted)', cursor: 'pointer' }}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function StarRow({ rating, size = 16 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[1,2,3,4,5].map(s => (
        <svg key={s} width={size} height={size} viewBox="0 0 24 24"
          fill={s <= Math.round(rating) ? '#e8a838' : '#e0e0e0'} stroke="none">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </span>
  );
}

// ── Size Guide Modal ───────────────────────────────────────────────────────────
const SIZE_DATA = [
  { size: 'XS',  chest: '32–33"', waist: '25–26"', hips: '35–36"', height: `5'0"–5'3"`, fit: 'Petite / Very Slim' },
  { size: 'S',   chest: '34–35"', waist: '27–28"', hips: '37–38"', height: `5'2"–5'5"`, fit: 'Slim' },
  { size: 'M',   chest: '36–37"', waist: '29–30"', hips: '39–40"', height: `5'4"–5'7"`, fit: 'Regular' },
  { size: 'L',   chest: '38–40"', waist: '31–33"', hips: '41–43"', height: `5'5"–5'8"`, fit: 'Regular / Full' },
  { size: 'XL',  chest: '41–43"', waist: '34–36"', hips: '44–46"', height: `5'6"–5'9"`, fit: 'Full' },
  { size: 'XXL', chest: '44–46"', waist: '37–39"', hips: '47–49"', height: `5'7"–5'10"`, fit: 'Plus' },
  { size: 'XXXL',chest: '47–50"', waist: '40–43"', hips: '50–53"', height: `5'8"–6'0"`, fit: 'Plus XL' },
];

function SizeGuideModal({ onClose, category }) {
  // Close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div onClick={handleBackdrop} style={{
      position: 'fixed', inset: 0, zIndex: 600,
      background: 'rgba(14,11,8,0.65)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', overflowY: 'auto',
    }}>
      <div style={{
        background: '#fff', width: '100%', maxWidth: 680,
        border: '1px solid var(--border)',
        maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '24px 28px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--cream)',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>
              Warbadobe Sizing
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, color: 'var(--ink)', lineHeight: 1 }}>
              Size Guide
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid var(--border)',
            width: 36, height: 36, cursor: 'pointer',
            fontSize: 16, color: 'var(--muted)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>×</button>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* How to measure */}
          <div style={{ marginBottom: 24, padding: 16, background: 'var(--cream-dark)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 10 }}>
              How to Measure
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              {[
                { label: 'Chest', desc: 'Measure around the fullest part of your chest, keeping tape parallel to ground.' },
                { label: 'Waist', desc: 'Measure around your natural waistline, just above the belly button.' },
                { label: 'Hips', desc: 'Measure around the fullest part of your hips and seat.' },
              ].map(m => (
                <div key={m.label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)', marginBottom: 3, letterSpacing: '0.06em' }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>{m.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Size table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--ink)' }}>
                  {['Size', 'Chest', 'Waist', 'Hips', 'Height', 'Fit Type'].map(h => (
                    <th key={h} style={{
                      padding: '10px 14px', textAlign: 'left', fontSize: 9,
                      fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SIZE_DATA.map((row, i) => (
                  <tr key={row.size} style={{ background: i % 2 === 0 ? '#fff' : 'var(--cream)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: 'var(--ink)', borderBottom: '1px solid var(--border)' }}>{row.size}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{row.chest}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{row.waist}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{row.hips}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{row.height}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{row.fit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tip */}
          <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--cream)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--ink)' }}>Between sizes?</strong> We recommend sizing up for a comfortable fit —
            especially for structured garments like sherwanis, blazers, and lehengas.
            If unsure, message us on WhatsApp before your event.
          </div>
        </div>
      </div>
    </div>
  );
}


const s = {
  layout:       { display: 'grid', gridTemplateColumns: 'clamp(280px, 45%, 520px) 1fr', gap: 'clamp(24px, 5vw, 60px)', alignItems: 'start' },
  mainImg:      { width: '100%', aspectRatio: '3/4', maxHeight: 520, border: '1px solid var(--border)', overflow: 'hidden' },
  thumb:        { width: 64, height: 64, border: '2px solid', cursor: 'pointer', overflow: 'hidden', flexShrink: 0 },
  productTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4vw, 42px)', marginTop: 12, marginBottom: 8 },
  sizeBtn:      { border: '1px solid', padding: '9px 16px', fontFamily: "'Josefin Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', transition: 'all 0.2s', minHeight: 44, minWidth: 44 },
  durationOption: { border: '1px solid', padding: '12px 16px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, minHeight: 50 },
  summary:      { background: 'var(--gold-pale)', border: '1px solid var(--gold)', padding: 18, marginTop: 18 },
  summaryRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' },
  ctaRow:       { display: 'flex', gap: 10, marginTop: 20 },
  wishBtn:      { background: 'transparent', border: '2px solid', fontSize: 22, padding: '10px 14px', cursor: 'pointer', transition: 'all 0.2s', lineHeight: 1, minHeight: 48, minWidth: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }
};